import React from "react";
import dayjs from "dayjs";
import { Trans } from "@lingui/macro";
import { getColor } from "../../../../utils";
import { Alert } from "../../../../UI/Alert/Alert";
import { useDispatch, useSelector } from "react-redux";
import { selectOrdersArchiveTableData } from "../../../../../model/dashboardOrdersArchive/selectors";
import { dOrdersArchiveSetDetailItemAC } from "../../../../../model/dashboardOrdersArchive/actionCreators";
import {
  getColorOrders,
  getTransStatus,
} from "../../../../../constants/ordersStatus";
import { numberCurrencyFormat } from "../../../../../constants/convertToLocalCurrency";

export type typeAction = {
  id: string;
  title: React.JSX.Element;
  disableSwitcher?: boolean;
  action: (id: string) => void;
};

export type typeTableBody = {
  variant?: string;
  setOpenPopupDetails: React.Dispatch<React.SetStateAction<boolean>>;
};

export type typeTableHeader = {
  variant?: string;
};

export const TableHeader: React.FC<typeTableHeader> = () => {
  return (
    <>
      <th
        scope="col"
        className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider"
      >
        <div className="flex flex-col">
          <span className="border-b">
            <Trans>Orders number</Trans>
          </span>
          <span>
            <Trans>Date</Trans>
          </span>
        </div>
      </th>
      <th
        scope="col"
        className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider"
      >
        <div className="flex flex-col">
          <span>
            <Trans>Date of sale</Trans>
          </span>
        </div>
      </th>
      <th
        scope="col"
        className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider"
      >
        <span className="whitespace-nowrap">
          <Trans>External order number</Trans>
        </span>
      </th>
      <th
        scope="col"
        className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider"
      >
        <span className="whitespace-nowrap">
          <Trans>Payment method</Trans>
        </span>
      </th>
      <th
        scope="col"
        className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider"
      >
        <Trans>Client</Trans>
      </th>
      <th
        scope="col"
        className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider"
      >
        <Trans>Total-Total</Trans>
      </th>

      <th
        scope="col"
        className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider"
      >
        <Trans>Status</Trans>
      </th>
    </>
  );
};

export const TableBody: React.FC<typeTableBody> = ({
  variant,
  setOpenPopupDetails,
}) => {
  const dispatch = useDispatch();
  const color = getColor(variant);

  const tableData = useSelector(selectOrdersArchiveTableData);

  const onCellClickHandler = (
    e: React.MouseEvent<HTMLTableDataCellElement, MouseEvent>,
    id: string,
  ) => {
    dispatch(dOrdersArchiveSetDetailItemAC(id));

    setOpenPopupDetails(true);
  };

  let component;

  if (tableData.length) {
    component = tableData.map((item, index) => {
      const payments = item?.payments || [];
      const statusColor = getColorOrders(item.status);

      let date = "-";
      if (item.dateCreated) {
        date = dayjs(item.dateCreated).format("YYYY-MM-DD, HH:mm");
      }

      let dateOfSale = "-";
      if (item?.dateCreated) {
        dateOfSale = dayjs(item.dateCreated).format("YYYY-MM-DD, HH:mm");
      }

      const transStatus = getTransStatus(item.status!);

      const total = item.payments.reduce((acc, curr) => {
        acc = acc + curr.paymentAmount;
        return acc;
      }, 0);

      return (
        <tr
          key={item.id}
          className={`${
            index % 2 === 0 ? "bg-white" : "bg-gray-50"
          } hover:bg-${color}-50`}
        >
          <td
            className="px-4 py-3 whitespace-nowrap w-1/4 text-sm font-sm text-gray-500 cursor-pointer"
            onClick={(e) => onCellClickHandler(e, item.id)}
          >
            <div className="flex flex-col">
              <span className="text-gray-900 font-normal border-b">
                {item.number ?? "-"}
              </span>
              <div>
                <span className="text-gray-500 text-xs leading-4  font-normal">
                  {date}
                </span>
              </div>
            </div>
          </td>
          <td
            className="px-4 py-3 whitespace-nowrap w-1/4 text-sm font-sm text-gray-500 cursor-pointer"
            onClick={(e) => onCellClickHandler(e, item.id)}
          >
            <div className="flex flex-col">
              <div>
                <span className="text-gray-500 text-xs leading-4 font-normal">
                  {dateOfSale ?? "-"}
                </span>
              </div>
            </div>
          </td>
          <td
            className="px-4 py-3 whitespace-wrap text-sm font-normal leading-5 text-gray-900 cursor-pointer"
            onClick={(e) => onCellClickHandler(e, item.id)}
          >
            <p className="truncate max-w-2xl">
              {item.externalId ? item.externalId : "-"}
            </p>
          </td>
          <td
            className="px-4 py-3 whitespace-wrap text-sm font-normal leading-5 text-gray-900 cursor-pointer"
            onClick={(e) => onCellClickHandler(e, item.id)}
          >
            {payments.length
              ? payments.map((payment, index) => {
                  return (
                    <div key={index}>
                      <span
                        className={`text-gray-900 font-normal ${
                          index + 1 < payments.length ? "border-b" : ""
                        }`}
                      >
                        {payment.paymentType}
                      </span>
                    </div>
                  );
                })
              : "-"}
          </td>
          <td
            className="px-4 py-3 whitespace-wrap text-sm font-normal leading-5 text-gray-900 cursor-pointer"
            onClick={(e) => onCellClickHandler(e, item.id)}
          >
            {item.customerName}
          </td>
          <td
            className="px-4 py-3 whitespace-wrap text-sm font-normal leading-5 text-gray-900 cursor-pointer"
            onClick={(e) => onCellClickHandler(e, item.id)}
          >
            {total ? numberCurrencyFormat(total) : "-"}
          </td>
          <td
            className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 cursor-pointer"
            onClick={(e) => onCellClickHandler(e, item.id)}
          >
            <div className="flex ">
              <span
                className={`bg-${statusColor} rounded w-2 h-2 mr-2 self-center`}
              />
              <span>{transStatus}</span>
            </div>
          </td>
        </tr>
      );
    });
  } else {
    component = (
      <tr>
        <td className="p-2" colSpan={10}>
          <Alert
            type="info"
            className="w-full"
            title={<Trans>The Orders list is empty.</Trans>}
          />
        </td>
      </tr>
    );
  }

  return <>{component}</>;
};
